/* eslint-disable max-lines */
import React, {
  useEffect, useState, useContext
} from 'react';
import {
  Button
} from 'shared/components/ui/button';
import {
  DialogFooter
} from 'shared/components/ui/dialog';
import {
  Label
} from 'shared/components/ui/label';
import {
  Input
} from 'shared/components/ui/input';
import {
  useParams
} from 'react-router-dom';
import {
  Context
} from 'shared/context/GlobalState';
import {
  channelService
} from 'shared/api';
import AssistantSelect from './AssistantSelect';
import CodeDisplay from 'shared/components/ui/CodeDisplay';
import {
  v4 as uuidv4
} from 'uuid';
import {
  server
} from 'shared/utils/httpsRequest';
import {
  PlusIcon, XIcon, Globe
} from 'lucide-react';
import {
  FaCheck, FaCopy
} from 'react-icons/fa';
import {
  ChannelSetupLayout
} from './ChannelSetupLayout';

import type {
  Channel, ChannelData
} from '../types/builderChannel.types';

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

interface NewChannelInputProps {
  newChannelObj?: Partial<ChannelData>;
  channelType?: string;
  onSubmit: (obj: ChannelData) => Promise<void>;
  setOpenEmailModal: (open: boolean) => void;
  isUpdate?: boolean;
}

export const NewChannelInput = ({
  newChannelObj = {},
  channelType,
  onSubmit,
  setOpenEmailModal,
  isUpdate = false,
}: NewChannelInputProps) => {
  const [channelUrls, setChannelUrls] = useState<string[]>((newChannelObj as any)?.origins || []);
  const [btnLoading, setBtnLoading] = useState(false);

  const [channelData, setChannelData] = useState<
    ChannelData & { origins?: string[]; includeConvoHistory?: boolean }
  >({
    type: 'webchat',
    topic: '',
    address: uuidv4(),
    assistant_id: '',
    origins: [],
    includeConvoHistory: false,
    ...newChannelObj,
  } as ChannelData & { origins?: string[]; includeConvoHistory?: boolean });

  const {
    addNewNotifcation
  } = useContext(Context);
  const {
    oragID, subOragID
  } = useParams<{ oragID: string; subOragID: string }>();

  const isButtonDisabled = () =>
    channelData.topic === '' || channelData.address === '' || !channelData.assistant_id;

  type ExtendedChannelData = ChannelData & { origins?: string[]; includeConvoHistory?: boolean };

  const handleChange = <K extends keyof ExtendedChannelData>(
    value: ExtendedChannelData[K],
    type: K
  ) => {
    setChannelData((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  useEffect(() => {
    handleChange(channelUrls, 'origins');
  }, [channelUrls]);

  const handleSubmit = () => {
    if (channelData.origins.some((item) => item === '' || !isValidUrl(item))) {
      addNewNotifcation('Please enter a valid URL', 'warning');
      return;
    }
    setBtnLoading(true);
    onSubmit(channelData).finally(() => setBtnLoading(false));
  };

  const code = `<script>
    (function() {
        window.widgetId = '${channelData.address}';
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '${server}/static/dist/assets/chat-widget.css?v=' + Date.now();
        link.type = 'text/css';
        document.head.appendChild(link);
        var script = document.createElement('script');
        script.src = '${server}/static/dist/assets/chat-widget.js?v=' + Date.now();
        script.async = true;
        script.setAttribute('data-widget', 'coolriots-chat-widget');
        script.onload = function() {};
        document.head.appendChild(script);
    })();
</script>`;

  return (
      <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
              <Label htmlFor="channel-type">Address</Label>
              <Input
          value={channelData.address}
          disabled={true}
          onChange={(e) => handleChange(e.target.value, 'address')}
        />
          </div>
          <div className="flex flex-col gap-4">
              <Label htmlFor="channel-type">Topic</Label>
              <Input
          disabled={!channelType}
          value={channelData.topic}
          onChange={(e) => handleChange(e.target.value, 'topic')}
          placeholder="Enquires, Reservation, ..."
        />
          </div>
          {!(newChannelObj as any)?.isAssistantDisabled && (
          <div className="flex flex-col gap-4">
              <Label htmlFor="channel-type">Assistant</Label>
              <AssistantSelect
          value={channelData.assistant_id}
          onChange={(value: string) => handleChange(value, 'assistant_id')}
        />
          </div>
          )}
          <div className="space-y-2 flex flex-col mt-3">
              <Label htmlFor="topic">Allowed Domains</Label>
              <span className="text-sm text-muted-foreground mt-1">
                  Add URLs to manage secure origins. Only listed URLs will be allowed.
              </span>
              <WorkflowMetadata data={channelUrls} setData={setChannelUrls} />
          </div>
          {isUpdate && (
          <div
          className="space-y-2"
          data-embed-wrapper="true"
          style={{
            outline: '2px dashed red',
            padding: 8
          }}
        >
              <Label>Embed Web Chat</Label>
              <CodeDisplay code={code} />
          </div>
      )}
          <DialogFooter className="mt-3">
              <Button onClick={() => setOpenEmailModal(false)} variant="outline">Cancel</Button>
              <Button onClick={handleSubmit} disabled={btnLoading || isButtonDisabled()}>Submit</Button>
          </DialogFooter>
      </div>
  );
};

interface WebChatChannelData {
  type: string;
  topic: string;
  address: string;
  assistantId: string;
  origins: string[];
  includeConvoHistory?: boolean;
  webChatConfig?: any;
  organizationId?: string;
  subOrganizationId?: string;
  _id?: string;
}

interface WebChatSetupProps {
  setOpenEmailModal: (open: boolean) => void;
  setEmailChannels?: React.Dispatch<React.SetStateAction<Channel[]>>;
  onBack?: () => void;
  isUpdate?: boolean;
  newChannelObj?: Partial<WebChatChannelData>;
  onSubmit?: (channel: WebChatChannelData) => Promise<void>;
}

export default function WebChatSetup({
  setOpenEmailModal,
  setEmailChannels,
  onBack,
  isUpdate = false,
  newChannelObj,
  onSubmit,
}: WebChatSetupProps) {
  const {
    oragID, subOragID
  } = useParams<{ oragID: string; subOragID: string }>();
  const {
    addNewNotifcation
  } = useContext(Context);

  const [btnLoading, setBtnLoading] = useState(false);
  const [widgetId] = useState(() => isUpdate ? (newChannelObj?.address ?? uuidv4()) : uuidv4());
  const [copiedId, setCopiedId] = useState(false);
  const [channelUrls, setChannelUrls] = useState<string[]>(
    isUpdate ? (newChannelObj?.origins ?? []) : []
  );
  const [channelData, setChannelData] = useState<WebChatChannelData>({
    type: 'webchat',
    topic: '',
    assistantId: '',
    origins: [],
    includeConvoHistory: false,
    ...newChannelObj,
    address: widgetId,
  });
  const handleChange = (value: string | boolean, type: string) =>
    setChannelData((prev) => ({
      ...prev,
      [type]: value
    }));

  const handleCopyId = () => {
    navigator.clipboard.writeText(widgetId).then(() => {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    });
  };

  const isButtonDisabled = () =>
    channelData.topic.trim() === '' || !channelData.assistantId;

  useEffect(() => {
    setChannelData((prev) => ({
      ...prev,
      origins: channelUrls
    }));
  }, [channelUrls]);

  const handleSubmit = async () => {
    if (channelUrls.some((url) => url === '' || !isValidUrl(url))) {
      addNewNotifcation('Please enter a valid URL', 'warning');
      return;
    }

    if (isUpdate && onSubmit) {
      setBtnLoading(true);
      try {
        await onSubmit(channelData);
      } finally {
        setBtnLoading(false);
      }
      return;
    }

    setBtnLoading(true);
    try {
      const payload = {
        org_id: oragID || '',
        sub_org_id: subOragID || '',
        address: channelData.address,
        topic: channelData.topic,
        type: channelData.type,
        provider: 'native',
        assistant_id: channelData.assistantId || '',
        receive_enabled: true,
        send_mode: 'auto' as const,
      };
      const res = await channelService.addChannel(payload);
      addNewNotifcation('Channel added successfully', 'success');
      setOpenEmailModal(false);
      setEmailChannels?.((prev) => [res.data, ...prev]);
    } catch (error: any) {
      if (error.response?.status === 409) {
        addNewNotifcation('Channel already exists', 'danger');
      } else if (error.response?.status === 403) {
        addNewNotifcation(
          error?.response?.data?.message || 'Resource limit. You can not create more channels under your current plan',
          'danger'
        );
      } else {
        addNewNotifcation('Something went wrong', 'danger');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const embedCode = `<script>
    (function() {
        window.widgetId = '${widgetId}';
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '${server}/static/dist/assets/chat-widget.css?v=' + Date.now();
        link.type = 'text/css';
        document.head.appendChild(link);
        var script = document.createElement('script');
        script.src = '${server}/static/dist/assets/chat-widget.js?v=' + Date.now();
        script.async = true;
        script.setAttribute('data-widget', 'coolriots-chat-widget');
        script.onload = function() {};
        document.head.appendChild(script);
    })();
</script>`;

  return (
      <ChannelSetupLayout
      onCancel={() => setOpenEmailModal(false)}
      onSubmit={handleSubmit}
      submitDisabled={isButtonDisabled()}
      loading={btnLoading}
      onBack={isUpdate ? undefined : onBack}
      title={isUpdate ? 'Update Web Chat' : 'Configure Web Chat'}
      isDashboard={true}
    >
          <div className="flex flex-col sm:flex-row w-full">
              <aside className="sm:w-[320px] shrink-0 bg-muted/40 border-b sm:border-b-0 sm:border-r border-border p-6 flex flex-col gap-5">
                  <div className="space-y-1">
                      <div className="inline-flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md text-primary mb-1">
                          <Globe size={14} />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Channel Configuration</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground px-1">Configure Instance</h3>
                  </div>
                  <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1">
                          <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                              Display Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                value={channelData.topic}
                onChange={(e) => handleChange(e.target.value, 'topic')}
                placeholder="Customer Support"
                className="h-9 text-xs"
              />
                      </div>
                      {!(newChannelObj as any)?.isAssistantDisabled && (
                      <div className="flex flex-col gap-1.5">
                          <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                              AI Assistant <span className="text-red-500">*</span>
                          </Label>
                          <AssistantSelect
                className="mt-0.5"
                value={channelData.assistantId}
                onChange={(value: string) => handleChange(value, 'assistantId')}
              />
                      </div>
                      )}
                      {isUpdate && (
                      <div className="flex items-center gap-2 pt-1">
                          <input
                  type="checkbox"
                  id="includeConvoHistory"
                  checked={channelData.includeConvoHistory ?? false}
                  onChange={(e) => handleChange(e.target.checked, 'includeConvoHistory')}
                  className="h-4 w-4 accent-primary"
                />
                          <Label htmlFor="includeConvoHistory" className="text-[11px] font-semibold text-muted-foreground cursor-pointer">
                              Include Conversation History
                          </Label>
                      </div>
                  )}
                  </div>
                  <div className="mt-auto pt-2">
                      <button
              type="button"
              onClick={() => window.open('https://coolriots.ai/webchat-channel/', '_blank')}
              className="text-[10px] font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 px-1 underline underline-offset-4"
            >
                          View Documentation
                      </button>
                  </div>
              </aside>

              <main className="flex-grow bg-background p-6 flex flex-col gap-5 overflow-y-auto">
                  <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
                      <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                          Widget ID {isUpdate && <span className="text-muted-foreground font-normal">(read-only)</span>}
                      </Label>
                      <div className="flex items-center gap-2 p-1.5 bg-muted border border-border rounded-lg">
                          <code className="flex-grow text-[11px] text-muted-foreground font-mono px-2 overflow-hidden text-ellipsis whitespace-nowrap">
                              {widgetId}
                          </code>
                          {!isUpdate && (
                          <Button type="button" variant="ghost" size="icon" onClick={handleCopyId} className="h-8 w-8">
                              {copiedId
                                ? <FaCheck className="text-green-500 size-3" />
                                : <FaCopy className="text-muted-foreground size-3" />}
                          </Button>
                      )}
                      </div>
                      {!isUpdate && (
                      <p className="text-[10px] text-muted-foreground px-1">
                          This ID is auto-generated and used to embed the widget on your site.
                      </p>
                  )}
                  </div>

                  {isUpdate && (
                  <div className="space-y-2">
                      <Label className="text-[11px] font-semibold text-muted-foreground ml-1">Embed Web Chat</Label>
                      <p className="text-[10px] text-muted-foreground px-1">
                          Copy and paste this snippet into your website to embed the chat widget.
                      </p>
                      <CodeDisplay code={embedCode} />
                  </div>
              )}

                  <div className="space-y-3">
                      <div>
                          <Label className="text-[11px] font-semibold text-muted-foreground ml-1">Allowed Domains</Label>
                          <p className="text-[10px] text-muted-foreground px-1 mt-1">
                              Only these URLs will be allowed to load the widget.
                          </p>
                      </div>
                      <WorkflowMetadata data={channelUrls} setData={setChannelUrls} />
                  </div>
              </main>
          </div>
      </ChannelSetupLayout>
  );
}

interface WorkflowMetadataProps {
  data: string[];
  setData: React.Dispatch<React.SetStateAction<string[]>>;
}

const WorkflowMetadata = ({
  data, setData
}: WorkflowMetadataProps) => {
  const [isEmpty, setIsEmpty] = useState(false);
  const {
    addNewNotifcation
  } = useContext(Context);

  useEffect(() => {
    setIsEmpty(data.some((str) => str === ''));
  }, [data]);

  const addMetadata = () => {
    if (data.some((str) => !isValidUrl(str))) {
      addNewNotifcation('Please enter a valid URL', 'warning');
      return;
    }
    setData([...data, '']);
  };

  const removeMetadata = (index: number) => {
    const updated = [...data];
    updated.splice(index, 1);
    setData(updated);
  };

  const handleMetadataChange = (index: number, value: string) => {
    const updated = [...data];
    updated[index] = value;
    setData(updated);
  };

  return (
      <div>
          <div className="flex flex-col gap-2">
              {data.map((item, index) => (
                  <div key={index} className="flex gap-2">
                      <Input
              className="flex-grow"
              placeholder="https://example.com"
              value={item}
              onChange={(e) => handleMetadataChange(index, e.target.value)}
            />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeMetadata(index)}>
                          <XIcon className="h-4 w-4" />
                          <span className="sr-only">Remove</span>
                      </Button>
                  </div>
              ))}
          </div>
          <Button
        disabled={isEmpty}
        type="button"
        className="mt-2"
        variant="ghost"
        onClick={addMetadata}
      >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add URL
          </Button>
      </div>
  );
};
